// WEB OTURUMUNUN HAREKETSİZLİK ZAMAN AŞIMI — istemci tarafı ortak durum (2026-10-04).
//
// İki zaman damgası localStorage'da tutulur ki AYNI tarayıcıdaki bütün sekmeler tek oturum gibi
// davransın (oturum çerezi zaten ortak):
//  - son KULLANICI etkinliği (klavye / işaretçi / dokunma / kaydırma): istemci zamanlayıcısı bundan sayar;
//  - sunucuya son DOKUNUŞ (arka plan olmayan her yanıt): canlı tutma pinginin gerekip gerekmediği.
// Çıkış ve "oturumu sürdür" BroadcastChannel ile diğer sekmelere duyurulur (yoksa storage olayı).
//
// localStorage erişimi gizli pencerede / engelli depolamada atabilir: her erişim korumalıdır ve
// depolama yoksa sekme kendi bellek içi değeriyle çalışır.

export const LAST_ACTIVITY_KEY = 'flexbee_last_activity';
export const LAST_SERVER_TOUCH_KEY = 'flexbee_last_server_touch';
const SIGNAL_KEY = 'flexbee_session_signal';
const CHANNEL_NAME = 'flexbee-session';

// Arka plan isteği başlığı — backend AuthenticationSetup.BackgroundRequestHeader ile AYNI.
// Açık bırakılmış sayfanın periyodik yoklaması (içe aktarma durumu, kalibrasyon ızgarası)
// kullanıcıyı "etkin" saymasın: sunucu pencerenin varlığını sorar ama UZATMAZ.
export const BACKGROUND_HEADER = 'X-Flexbee-Background';
export const backgroundRequest = (config = {}) => ({
  ...config,
  headers: { ...(config.headers || {}), [BACKGROUND_HEADER]: '1' },
});

export const isBackgroundRequest = (config) => {
  const headers = config?.headers;
  if (!headers) return false;
  const value = typeof headers.get === 'function' ? headers.get(BACKGROUND_HEADER) : headers[BACKGROUND_HEADER];
  return value === '1' || value === 1;
};

// Etkin ama istek atmayan kullanıcı için ping aralığı. Sunucu payından (SessionSecurityPolicy
// .IdleGraceMinutes = 5 dk) KÜÇÜK olmalı — gerekçe orada.
export const KEEPALIVE_AFTER_MS = 4 * 60 * 1000;

// NIST 800-63B-4 kullanılabilirlik bölümü ve ADP: zaman aşımından ~2 dk önce uyarı.
// WCAG 2.2.1: uyarı en az 20 sn önce ve tek hareketle uzatılabilir olmalı.
export const WARNING_LEAD_MS = 2 * 60 * 1000;

const memory = { [LAST_ACTIVITY_KEY]: 0, [LAST_SERVER_TOUCH_KEY]: 0 };

const read = (key) => {
  try {
    const value = Number(window.localStorage.getItem(key));
    if (Number.isFinite(value) && value > 0) return value;
  } catch { /* depolama yok */ }
  return memory[key] || 0;
};

const write = (key, value) => {
  memory[key] = value;
  try {
    window.localStorage.setItem(key, String(value));
  } catch { /* depolama yok */ }
};

export const getLastActivity = () => read(LAST_ACTIVITY_KEY);
export const getLastServerTouch = () => read(LAST_SERVER_TOUCH_KEY);

// Fare hareketi saniyede onlarca olay üretir; yazımı seyrekleştir (damga en fazla 5 sn bayat
// kalır — zaman aşımı en fazla 5 sn erken dolar).
const ACTIVITY_WRITE_THROTTLE_MS = 5000;
let lastActivityWrite = 0;
export const markActivity = (now = Date.now()) => {
  if (now - lastActivityWrite < ACTIVITY_WRITE_THROTTLE_MS) return;
  lastActivityWrite = now;
  write(LAST_ACTIVITY_KEY, now);
};

// Throttle'sız: "oturumu sürdür" ve giriş anı kesin damga ister.
export const markActivityNow = (now = Date.now()) => {
  lastActivityWrite = now;
  write(LAST_ACTIVITY_KEY, now);
};

export const markServerTouch = (now = Date.now()) => write(LAST_SERVER_TOUCH_KEY, now);

export const clearSessionActivity = () => {
  lastActivityWrite = 0;
  memory[LAST_ACTIVITY_KEY] = 0;
  memory[LAST_SERVER_TOUCH_KEY] = 0;
  try {
    window.localStorage.removeItem(LAST_ACTIVITY_KEY);
    window.localStorage.removeItem(LAST_SERVER_TOUCH_KEY);
  } catch { /* depolama yok */ }
};

// ── Sekmeler arası sinyal ───────────────────────────────────────────────────

let channel = null;
const getChannel = () => {
  if (channel !== null) return channel;
  try {
    channel = typeof BroadcastChannel === 'function' ? new BroadcastChannel(CHANNEL_NAME) : false;
  } catch {
    channel = false;
  }
  return channel;
};

// type: 'idle-logout' | 'logout' | 'continue'
export const broadcastSessionSignal = (type) => {
  const message = { type, at: Date.now() };
  const ch = getChannel();
  if (ch) {
    try { ch.postMessage(message); } catch { /* kapalı kanal */ }
    return;
  }
  try {
    window.localStorage.setItem(SIGNAL_KEY, JSON.stringify(message));
  } catch { /* depolama yok */ }
};

export const subscribeSessionSignal = (handler) => {
  const ch = getChannel();
  if (ch) {
    const onMessage = (e) => { if (e?.data?.type) handler(e.data.type); };
    ch.addEventListener('message', onMessage);
    return () => ch.removeEventListener('message', onMessage);
  }
  const onStorage = (e) => {
    if (e.key !== SIGNAL_KEY || !e.newValue) return;
    try {
      const message = JSON.parse(e.newValue);
      if (message?.type) handler(message.type);
    } catch { /* bozuk değer */ }
  };
  window.addEventListener('storage', onStorage);
  return () => window.removeEventListener('storage', onStorage);
};
